import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';
import Header from '../common/Header.js';
import Sidebar from '../common/Sidebar.js';
import Alert from '../common/Alert.js';
import billingService from '../../services/BillingService.js';
import billingPaymentService from '../../services/BillingPaymentService.js';
import AddPaymentModal from './AddPaymentModal.js';
import EditPaymentModal from './EditPaymentModal.js';
import ViewPaymentModal from './ViewPaymentModal.js';
import tankCleaningService from '../../services/TankCleaningService.js';
import dryBoxSurveyService from '../../services/DryBoxSurveyService.js';
import tiReportService from '../../services/TIReportService.js';
import eiReceiptService from '../../services/EIReceiptService.js';
import onhireService from '../../services/OnHireService.js';
import {
  FileText, ArrowLeft, Pencil, Download, CheckCircle, XCircle,
  Plus, Trash2, Eye, IndianRupee, Calendar, MapPin, Tag,
  ClipboardList, Package, Wrench, FileCheck, ReceiptText,
  Anchor, ChevronDown, ChevronUp, CreditCard, AlertCircle,
  Building2, Phone, Mail, X, Save, Loader2, Landmark, Percent
} from 'lucide-react';

// ─── Constants ──────────────────────────────────────────────────────────────

const PAYMENT_METHODS = ['Cash', 'Bank Transfer', 'Cheque', 'UPI', 'NEFT', 'RTGS', 'DD'];

const SERVICE_CONFIG = {
  tank_cleaning: {
    label: 'Tank Cleanings',
    icon: Wrench,
    color: 'blue',
    idField: 'tank_cleaning_ids',
    qtyField: 'tank_cleaning_qty',
    amountField: 'tank_cleaning_amount',
    rateField: 'tank_cleaning_rate',
  },
  dry_box: {
    label: 'Dry Box Surveys',
    icon: Package,
    color: 'green',
    idField: 'dry_box_ids',
    qtyField: 'dry_box_qty',
    amountField: 'dry_box_amount',
    rateField: 'dry_box_rate',
  },
  ti_report: {
    label: 'TI Reports',
    icon: FileCheck,
    color: 'purple',
    idField: 'ti_report_ids',
    qtyField: 'ti_report_qty',
    amountField: 'ti_report_amount',
    rateField: 'ti_report_rate',
  },
  ei_receipt: {
    label: 'EI Receipts',
    icon: ReceiptText,
    color: 'orange',
    idField: 'ei_receipt_ids',
    qtyField: 'ei_receipt_qty',
    amountField: 'ei_receipt_amount',
    rateField: 'ei_receipt_rate',
  },
  onhire: {
    label: 'OnHires',
    icon: Anchor,
    color: 'teal',
    idField: 'onhire_ids',
    qtyField: 'onhire_qty',
    amountField: 'onhire_amount',
    rateField: 'onhire_rate',
  },
};

const COLOR_CLASSES = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   text: 'text-blue-700',   badge: 'bg-blue-100 text-blue-700',   icon: 'text-blue-500'   },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  text: 'text-green-700',  badge: 'bg-green-100 text-green-700', icon: 'text-green-500'  },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', icon: 'text-purple-500' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-700', icon: 'text-orange-500' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   text: 'text-teal-700',   badge: 'bg-teal-100 text-teal-700',   icon: 'text-teal-500'   },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const formatCurrency = (amount) => {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 2 }).format(num);
};

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
};

const formatDateShort = (dateString) => {
  if (!dateString) return 'N/A';
  return new Date(dateString).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

// Status Badge
const StatusBadge = ({ status }) => {
  const cfg = {
    paid:         { bg: 'bg-green-100',  text: 'text-green-700',  label: 'Paid',         icon: CheckCircle  },
    unpaid:       { bg: 'bg-red-100',    text: 'text-red-700',    label: 'Unpaid',        icon: XCircle      },
    partial_paid: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Partial Paid',  icon: AlertCircle  },
  }[status] || { bg: 'bg-gray-100', text: 'text-gray-700', label: status, icon: AlertCircle };

  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${cfg.bg} ${cfg.text}`}>
      <Icon className="w-3.5 h-3.5" />
      {cfg.label}
    </span>
  );
};

// Info Row
const InfoRow = ({ label, value, className = '' }) => (
  <div className={className}>
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">{label}</p>
    <p className="text-sm font-semibold text-secondary break-words">{value || 'N/A'}</p>
  </div>
);

// Section Header
const SectionHeader = ({ icon: Icon, title, count, color = 'orange', children }) => (
  <div className="flex items-center justify-between">
    <h3 className="text-base font-semibold text-secondary flex items-center gap-2">
      {Icon && <Icon className={`w-4 h-4 text-buttonBg`} />}
      {title}
      {count !== undefined && (
        <span className="text-xs font-medium bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{count}</span>
      )}
    </h3>
    {children}
  </div>
);

// ─── Service Records Table ─────────────────────────────────────────────────

const ServiceRecordsTable = ({ serviceKey, billing, navigate }) => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(true);

  const cfg = SERVICE_CONFIG[serviceKey];
  const colors = COLOR_CLASSES[cfg.color];
  const ids = billing[cfg.idField] || [];
  const Icon = cfg.icon;

  useEffect(() => {
    if (ids.length === 0) return;
    fetchRecords();
  }, []);

  const fetchRecords = async () => {
    setLoading(true);
    try {
      const fetchers = {
        tank_cleaning: (id) => tankCleaningService.getById(id),
        dry_box:       (id) => dryBoxSurveyService.getById(id),
        ti_report:     (id) => tiReportService.getById(id),
        ei_receipt:    (id) => eiReceiptService.getById(id),
        onhire:        (id) => onhireService.getById(id),
      };
      const fetcher = fetchers[serviceKey];
      const results = await Promise.all(ids.map(id => fetcher(id).then(r => r.data).catch(() => null)));
      setRecords(results.filter(Boolean));
    } catch (err) {
      console.error(`Failed to fetch ${cfg.label}`, err);
    } finally {
      setLoading(false);
    }
  };

  const getViewPath = (id) => {
    const paths = {
      tank_cleaning: `/tank-cleanings/view/${id}`,
      dry_box:       `/dry-box-surveys/view/${id}`,
      ti_report:     `/ti-reports/view/${id}`,
      ei_receipt:    `/ei-receipts/view/${id}`,
      onhire:        `/onhires/view/${id}`,
    };
    return paths[serviceKey];
  };

  const getRecordId = (record) => {
    const fields = {
      tank_cleaning: 'cleaning_id',
      dry_box:       'survey_id',
      ti_report:     'report_id',
      ei_receipt:    'receipt_id',
      onhire:        'onhire_id',
    };
    return record[fields[serviceKey]] || `#${record.id}`;
  };

  if (ids.length === 0) return null;

  return (
    <div className={`border ${colors.border} rounded-lg overflow-hidden`}>
      {/* Header */}
      <div
        className={`${colors.bg} px-4 py-3 flex items-center justify-between cursor-pointer`}
        onClick={() => setExpanded(p => !p)}
      >
        <div className="flex items-center gap-3">
          <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${colors.badge}`}>
            <Icon className="w-4 h-4" />
          </div>
          <div>
            <span className={`font-semibold text-sm ${colors.text}`}>{cfg.label}</span>
            <span className="ml-2 text-xs text-gray-500">{ids.length} record{ids.length !== 1 ? 's' : ''}</span>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-gray-500">Rate × Qty</p>
            <p className={`text-sm font-bold ${colors.text}`}>
              {formatCurrency(billing[cfg.rateField] || 0)} × {billing[cfg.qtyField] || 0} = {formatCurrency(billing[cfg.amountField] || 0)}
            </p>
          </div>
          {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Table */}
      {expanded && (
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-6 flex items-center justify-center gap-2 text-gray-500">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">Loading records...</span>
            </div>
          ) : records.length > 0 ? (
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Record ID</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Container / Tank</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Location</th>
                  <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.map((record, idx) => (
                  <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-2.5 text-gray-400 text-xs">{idx + 1}</td>
                    <td className="px-4 py-2.5">
                      <span className={`font-mono text-xs font-semibold ${colors.text}`}>
                        {getRecordId(record)}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-gray-700 font-medium text-xs">
                      {record.container_no || record.tank_no || record.container_number || '—'}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {formatDateShort(
                        record.survey_date || 
                        record.inspection_date || 
                        record.cleaning_date || 
                        record.onhire_date || 
                        record.report_date || 
                        record.receipt_date || 
                        record.issue_date ||
                        record.date || 
                        record.created_at
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-gray-600 text-xs">
                      {record.location 
                        ? `${record.location.city}${record.location.state ? `, ${record.location.state}` : ''}` 
                        : (record.location_name || record.depot || '—')
                      }
                    </td>
                    <td className="px-4 py-2.5">
                      {record.published ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                          <CheckCircle className="w-3 h-3" /> Published
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                          <XCircle className="w-3 h-3" /> Draft
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <button
                        onClick={() => navigate(getViewPath(record.id))}
                        className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-xs font-medium ${colors.badge} hover:opacity-80 transition-opacity`}
                      >
                        <Eye className="w-3 h-3" />
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="p-6 text-center text-gray-400 text-sm">
              Records could not be loaded.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Delete Confirmation Modal ───────────────────────────────────────────────

const DeleteConfirmModal = ({ title, message, onConfirm, onClose, loading }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
    <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm p-6">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
          <Trash2 className="w-5 h-5 text-red-600" />
        </div>
        <h3 className="text-base font-semibold text-secondary">{title}</h3>
      </div>
      <p className="text-sm text-gray-500 mb-5">{message}</p>
      <div className="flex gap-3">
        <button
          onClick={onClose}
          className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="flex-1 py-2.5 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-60 flex items-center justify-center gap-2"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          Delete
        </button>
      </div>
    </div>
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const ViewBilling = () => {
  const { billingId } = useParams();
  const [billing, setBilling] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading]  = useState(true);
  const [paymentsLoading, setPaymentsLoading] = useState(false);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [paymentModal, setPaymentModal] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [viewPayment, setViewPayment] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();
  const isClient = user?.role_name === 'Client';

  // ─── Fetch billing ─────────────────────────────────────────────────────────
  useEffect(() => {
    fetchBilling();
  }, [billingId]);

  const fetchBilling = async () => {
    try {
      const response = await billingService.getById(billingId);
      setBilling(response.data);
      await fetchPayments(response.data.id);
    } catch (err) {
      Alert.error('Failed to load billing details');
      console.error(err);
      navigate('/billings');
    } finally {
      setLoading(false);
    }
  };

  const fetchPayments = useCallback(async (pk) => {
    setPaymentsLoading(true);
    try {
      const response = await billingPaymentService.getByBilling(pk);
      setPayments(response.data || []);
    } catch (err) {
      console.error('Failed to load payments', err);
    } finally {
      setPaymentsLoading(false);
    }
  }, []);

  // ─── Invoice download ──────────────────────────────────────────────────────
  const handleDownloadInvoice = async () => {
    setInvoiceLoading(true);
    try {
      const blob = await billingService.generateInvoice(billingId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `invoice_${billing.billing_id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      Alert.success('Invoice downloaded successfully!');
    } catch (err) {
      Alert.error('Failed to download invoice');
    } finally {
      setInvoiceLoading(false);
    }
  };

  // ─── Payment CRUD ──────────────────────────────────────────────────────────
  const handlePaymentSaved = () => {
    setPaymentModal(false);
    setEditPayment(null);
    fetchBilling(); // Refresh billing (status & balance may change)
  };

  const handleDeletePayment = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await billingPaymentService.delete(deleteTarget.id);
      Alert.success('Payment deleted successfully!');
      setDeleteTarget(null);
      fetchBilling();
    } catch (err) {
      Alert.error('Failed to delete payment');
    } finally {
      setDeleteLoading(false);
    }
  };

  // ─── Computed values ───────────────────────────────────────────────────────
  const subtotal = billing
    ? (billing.tank_cleaning_amount || 0) + (billing.dry_box_amount || 0) +
      (billing.ti_report_amount || 0) + (billing.ei_receipt_amount || 0) + (billing.onhire_amount || 0)
    : 0;
  const otherCharges = billing ? (billing.other_charges || 0) : 0;

  // ─── Loading state ─────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="max-w-[1850px] mx-auto">
        <Header />
        <div className="flex">
          <Sidebar />
          <div className="flex bg-light w-full mt-16 sm:mt-0">
            <div className="flex-1 flex flex-col overflow-hidden">
              <main className="flex-1 flex items-center justify-center p-6">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buttonBg mx-auto mb-4" />
                  <p className="text-sm text-gray-600">Loading billing details...</p>
                </div>
              </main>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!billing) return null;

  const hasAnyServices = Object.values(SERVICE_CONFIG).some(
    cfg => (billing[cfg.idField] || []).length > 0
  );

  return (
    <div className="max-w-[1850px] mx-auto">
      <Header />
      <div className="flex">
        <Sidebar />
        <div className="flex bg-light w-full mt-16 sm:mt-0">
          <div className="flex-1 flex flex-col overflow-hidden">
            <main className="flex-1 overflow-auto p-3 sm:p-4 md:p-6">
              <div className="max-w-8xl mx-auto">
                {/* ── Page Header ──────────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-4">
                  <div>
                    <h1 className="text-2xl sm:text-xl font-bold text-secondary flex items-center gap-2 sm:gap-3">
                      <FileText className="w-5 h-5 text-buttonBg" />
                      Billing Details
                    </h1>
                    <p className="text-xs text-gray-500 mt-0.5">View complete billing record and service information</p>
                  </div>
                  <button
                    onClick={() => navigate('/billings')}
                    className="relative overflow-hidden group flex items-center justify-center gap-2 px-3 py-2.5 text-xs sm:text-sm rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt font-medium whitespace-nowrap transition-colors duration-300 ease-out hover:bg-transparent hover:text-buttonBg"
                  >
                    <span className="absolute inset-0 bg-buttonBg scale-x-100 group-hover:scale-x-0 origin-center transition-transform duration-300 ease-out z-0" />
                    <ArrowLeft className="w-4 h-4 relative z-10" />
                    <span className="relative z-10">Back to Billings</span>
                  </button>
                </div>

                {/* ── Billing Header Card ───────────────────────────────────── */}
                <div className="bg-white rounded-sm shadow-sm overflow-hidden">
                  <div className="bg-primary p-3 px-4">
                    <div className="flex flex-col lg:flex-row items-start lg:items-center gap-3 lg:gap-4">
                      <div className="flex-1 flex flex-wrap items-center gap-3">
                        <h2 className="text-xl sm:text-lg font-semibold text-secondary">
                          Billing #{billing.billing_id}
                        </h2>
                        <StatusBadge status={billing.billing_status} />
                        <span className="text-xs px-2 py-1 bg-white/60 rounded font-medium text-gray-600">
                          {billing.billing_type}
                        </span>
                      </div>
                      {!isClient && (
                        <div className="flex flex-wrap gap-2">
                          <button
                            onClick={() => navigate(`/billings/edit/${billingId}`)}
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-white border border-gray-200 text-secondary hover:bg-gray-50 transition-colors"
                          >
                            <Pencil className="w-3.5 h-3.5" />
                            Edit
                          </button>
                          <button
                            onClick={handleDownloadInvoice}
                            disabled={invoiceLoading}
                            className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg bg-buttonBg text-buttonTxt hover:opacity-90 transition-opacity disabled:opacity-60"
                          >
                            {invoiceLoading ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : (
                              <Download className="w-3.5 h-3.5" />
                            )}
                            {invoiceLoading ? 'Generating...' : 'Download Invoice'}
                          </button>
                        </div>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1.5">Created on {formatDate(billing.created_at)}</p>
                  </div>

                  {/* ── Billing Info + Amount Summary ─────────────────────── */}
                  <div className="p-4 grid grid-cols-1 lg:grid-cols-5 gap-4">

                    {/* Billing Information */}
                    <div className="lg:col-span-2 space-y-4">
                      <h3 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100">
                        Billing Information
                      </h3>
                      <div className="grid grid-cols-2 gap-3">
                        <InfoRow label="Billing Date" value={formatDate(billing.billing_date)} />
                        <InfoRow label="Billing Type" value={billing.billing_type} />
                        <InfoRow label="Location" value={billing.location_name || billing.location?.city} />
                        <InfoRow label="State" value={billing.state || billing.location?.state} />
                        {billing.sac_code && (
                          <InfoRow label="SAC Code" value={billing.sac_code} />
                        )}
                        <InfoRow label="Created By" value={billing.created_by} />
                      </div>
                      {billing.description && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Description</p>
                          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100">
                            {billing.description}
                          </p>
                        </div>
                      )}
                      {billing.other_description && (
                        <div>
                          <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Other Description</p>
                          <p className="text-sm text-gray-700 bg-gray-50 rounded-lg p-3 border border-gray-100">
                            {billing.other_description}
                          </p>
                        </div>
                      )} 
                    </div>

                    {/* Client Details */}
                    <div className="lg:col-span-2 space-y-4">
                      <h3 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100">
                        Client Details
                      </h3>
                      {billing.client ? (
                        <div className="space-y-3">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 rounded-full bg-buttonBg/10 flex items-center justify-center flex-shrink-0">
                              <Building2 className="w-4 h-4 text-buttonBg" />
                            </div>
                            <div>
                              <p className="text-sm font-bold text-secondary">{billing.client.name}</p>
                              <p className="text-xs text-gray-500">{billing.client.user_id}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 gap-2 text-xs">
                            {billing.client.organization_name && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Building2 className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span>{billing.client.organization_name}</span>
                              </div>
                            )}
                            {billing.client.gst_no && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Tag className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span>GST: {billing.client.gst_no}</span>
                              </div>
                            )}
                            {billing.client.contact && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Phone className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span>{billing.client.contact}</span>
                              </div>
                            )}
                            {billing.client.email && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <Mail className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span className="break-all">{billing.client.email}</span>
                              </div>
                            )}
                            {billing.client.city && (
                              <div className="flex items-center gap-2 text-gray-600">
                                <MapPin className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                                <span>{billing.client.city}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <p className="text-sm text-gray-400">No client data</p>
                      )}
                    </div>

                    {/* Amount Summary */}
                    <div className="lg:col-span-1 space-y-3">
                      <h3 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100">
                        Amount Summary
                      </h3>

                      {/* Service breakdown */}
                      <div className="space-y-1.5">
                        {Object.entries(SERVICE_CONFIG).map(([key, cfg]) => {
                          const ids = billing[cfg.idField] || [];
                          if (ids.length === 0) return null;
                          const colors = COLOR_CLASSES[cfg.color];
                          return (
                            <div key={key} className="flex justify-between items-center text-xs">
                              <span className={`${colors.text} font-medium`}>
                                {cfg.label} ({ids.length})
                              </span>
                              <span className="text-gray-700 font-medium">
                                {formatCurrency(billing[cfg.amountField] || 0)}
                              </span>
                            </div>
                          );
                        })}
                      </div>

                      <div className="border-t border-gray-100 pt-2 space-y-1.5">
                        <div className="flex justify-between text-xs">
                          <span className="text-gray-500">Services Subtotal</span>
                          <span className="font-semibold">{formatCurrency(subtotal)}</span>
                        </div>
                        {otherCharges > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Additional Charges</span>
                            <span className="font-semibold">{formatCurrency(otherCharges)}</span>
                          </div>
                        )}
                        {billing.cgst > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">CGST ({billing.cgst}%)</span>
                            <span className="font-semibold">{formatCurrency(billing.cgst_amount)}</span>
                          </div>
                        )}
                        {billing.sgst > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">SGST ({billing.sgst}%)</span>
                            <span className="font-semibold">{formatCurrency(billing.sgst_amount)}</span>
                          </div>
                        )}
                        {billing.igst > 0 && (
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">IGST ({billing.igst}%)</span>
                            <span className="font-semibold">{formatCurrency(billing.igst_amount)}</span>
                          </div>
                        )}
                      </div>

                      <div className="bg-gray-50 rounded-lg p-3 space-y-2">
                        <div className="flex justify-between items-center">
                          <span className="text-xs font-bold text-gray-700">Total Amount</span>
                          <span className="text-base font-bold text-buttonBg">{formatCurrency(billing.total)}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-green-600 font-medium">Already Paid</span>
                          <span className="text-sm font-bold text-green-600">{formatCurrency(billing.paid)}</span>
                        </div>
                        <div className="border-t border-gray-200 pt-2 flex justify-between items-center">
                          <span className="text-xs font-bold text-red-600">Remaining Balance</span>
                          <span className="text-sm font-bold text-red-600">{formatCurrency(billing.balance_amount)}</span>
                        </div>
                      </div>

                      {/* Bank Payment + TDS breakdown */}
                      <div className="grid grid-cols-2 gap-2 mt-1">
                        <div className="bg-indigo-50 border border-indigo-100 rounded-lg p-2.5 flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <Landmark className="w-3.5 h-3.5 text-indigo-500" />
                            <span className="text-xs font-semibold text-indigo-700">Bank Payment</span>
                          </div>
                          <span className="text-sm font-bold text-indigo-700">
                            {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.bank_payment) || 0), 0))}
                          </span>
                        </div>
                        <div className="bg-purple-50 border border-purple-100 rounded-lg p-2.5 flex flex-col gap-1">
                          <div className="flex items-center gap-1.5">
                            <Percent className="w-3.5 h-3.5 text-purple-500" />
                            <span className="text-xs font-semibold text-purple-700">TDS</span>
                          </div>
                          <span className="text-sm font-bold text-purple-700">
                            {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.tds) || 0), 0))}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* ── Service Records ───────────────────────────────────────── */}
                {hasAnyServices && (
                  <div className="bg-white rounded-sm shadow-sm p-4">
                    <SectionHeader
                      icon={ClipboardList}
                      title="Service Records"
                      count={Object.values(SERVICE_CONFIG).reduce((acc, cfg) => acc + (billing[cfg.idField]?.length || 0), 0)}
                    />
                    <p className="text-xs text-gray-500 mt-1 mb-4">All services linked to this billing</p>

                    <div className="space-y-3">
                      {Object.keys(SERVICE_CONFIG).map(key => (
                        <ServiceRecordsTable
                          key={key}
                          serviceKey={key}
                          billing={billing}
                          navigate={navigate}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Payment Records ───────────────────────────────────────── */}
                <div className="bg-white rounded-sm shadow-sm p-4">
                  {/* Header */}
                  <div className="flex items-center justify-between mb-4">
                    <SectionHeader icon={CreditCard} title="Payment Records" count={payments.length} />
                    {!isClient && (
                      <button
                        onClick={() => { setEditPayment(null); setPaymentModal(true); }}
                        className="relative overflow-hidden group flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg border border-buttonBg bg-buttonBg text-buttonTxt hover:bg-transparent hover:text-buttonBg transition-colors duration-200"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        Record Payment
                      </button>
                    )}
                  </div>

                  {/* Summary Stats */}
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3 text-center border border-gray-100">
                      <p className="text-lg font-bold text-secondary">{payments.length}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Payments Made</p>
                    </div>
                    <div className="bg-green-50 rounded-lg p-3 text-center border border-green-100">
                      <p className="text-lg font-bold text-green-600">{formatCurrency(billing.paid)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Total Paid</p>
                    </div>
                    <div className="bg-red-50 rounded-lg p-3 text-center border border-red-100">
                      <p className="text-lg font-bold text-red-600">{formatCurrency(billing.balance_amount)}</p>
                      <p className="text-xs text-gray-500 mt-0.5">Pending</p>
                    </div>
                    <div className="bg-indigo-50 rounded-lg p-3 text-center border border-indigo-100">
                      <p className="text-lg font-bold text-indigo-600">
                        {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.bank_payment) || 0), 0))}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">Bank Payment</p>
                    </div>
                    <div className="bg-purple-50 rounded-lg p-3 text-center border border-purple-100">
                      <p className="text-lg font-bold text-purple-600">
                        {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.tds) || 0), 0))}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">TDS</p>
                    </div>
                  </div>

                  {/* Payment Table */}
                  {paymentsLoading ? (
                    <div className="flex items-center justify-center py-10 gap-2 text-gray-500">
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span className="text-sm">Loading payments...</span>
                    </div>
                  ) : payments.length > 0 ? (
                    <div className="overflow-x-auto rounded-lg border border-gray-100">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50 border-b border-gray-100">
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">#</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Bank Pmt</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">TDS</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Method</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Reference No.</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Notes</th>
                            {!isClient && (
                              <th className="px-4 py-3 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                            )}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-50">
                          {payments.map((payment, idx) => (
                            <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-gray-400 text-xs">{idx + 1}</td>
                              <td className="px-4 py-3">
                                <div className="flex items-center gap-1.5 text-xs text-gray-700">
                                  <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                  {formatDateShort(payment.payment_date)}
                                </div>
                              </td>
                              <td className="px-4 py-3">
                                <span className="font-bold text-green-600 text-sm">
                                  {formatCurrency(payment.amount)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs font-semibold text-indigo-600">
                                  {formatCurrency(payment.bank_payment || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="text-xs font-semibold text-purple-600">
                                  {formatCurrency(payment.tds || 0)}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700">
                                  <CreditCard className="w-3 h-3" />
                                  {payment.payment_mode || '—'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-600 font-mono">
                                {payment.reference_no || '—'}
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-500 max-w-[150px] truncate" title={payment.remarks}>
                                {payment.remarks || '—'}
                              </td>
                              {!isClient && (
                                <td className="px-4 py-3 text-center">
                                  <div className="flex items-center justify-center gap-1.5">
                                    <button
                                      onClick={() => setViewPayment(payment)}
                                      className="p-1.5 rounded-lg bg-green-50 text-green-600 hover:bg-green-100 transition-colors"
                                      title="View payment details"
                                    >
                                      <Eye className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => { setEditPayment(payment); setPaymentModal(true); }}
                                      className="p-1.5 rounded-lg bg-blue-50 text-blue-600 hover:bg-blue-100 transition-colors"
                                      title="Edit payment"
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => setDeleteTarget(payment)}
                                      className="p-1.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                                      title="Delete payment"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </div>
                                </td>
                              )}
                            </tr>
                          ))}
                        </tbody>
                        {/* Footer total */}
                        <tfoot>
                          <tr className="bg-green-50 border-t-2 border-green-200">
                            <td colSpan={2} className="px-4 py-2.5 text-xs font-bold text-gray-700 uppercase tracking-wider">
                              Totals
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-sm font-bold text-green-700">
                                {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0))}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-sm font-bold text-indigo-700">
                                {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.bank_payment) || 0), 0))}
                              </span>
                            </td>
                            <td className="px-4 py-2.5">
                              <span className="text-sm font-bold text-purple-700">
                                {formatCurrency(payments.reduce((sum, p) => sum + (parseFloat(p.tds) || 0), 0))}
                              </span>
                            </td>
                            <td colSpan={!isClient ? 4 : 3} />
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-center border border-dashed border-gray-200 rounded-lg">
                      <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mb-3">
                        <IndianRupee className="w-6 h-6 text-gray-400" />
                      </div>
                      <p className="text-sm font-semibold text-gray-600 mb-1">No payments recorded yet</p>
                      <p className="text-xs text-gray-400 mb-4">Record the first payment using the button above</p>
                      {!isClient && (
                        <button
                          onClick={() => { setEditPayment(null); setPaymentModal(true); }}
                          className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded-lg bg-buttonBg text-buttonTxt hover:opacity-90 transition-opacity"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          Record First Payment
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {/* ── System Information ────────────────────────────────────── */}
                <div className="bg-white rounded-sm shadow-sm p-4">
                  <h3 className="text-sm font-semibold text-buttonBg pb-2 border-b border-gray-100 mb-3">
                    System Information
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                    <InfoRow label="Created By" value={billing.created_by} />
                    <InfoRow label="Created At" value={formatDate(billing.created_at)} />
                    <InfoRow label="Last Updated" value={formatDate(billing.updated_at)} />
                    <InfoRow label="Billing ID" value={billing.billing_id} />
                  </div>
                </div>

              </div>
            </main>
          </div>
        </div>
      </div>

      {/* ── Modals ─────────────────────────────────────────────────────────── */}
      {paymentModal && !editPayment && (
        <AddPaymentModal
          isOpen={paymentModal}
          onClose={() => { setPaymentModal(false); setEditPayment(null); }}
          billing={{
            id: billing.id,
            total_amount: billing.total,
            total_paid: billing.paid
          }}
          onPaymentAdded={handlePaymentSaved}
        />
      )}

      {paymentModal && editPayment && (
        <EditPaymentModal
          isOpen={paymentModal}
          onClose={() => { setPaymentModal(false); setEditPayment(null); }}
          payment={editPayment}
          billing={{
            id: billing.id,
            total_amount: billing.total,
            total_paid: billing.paid
          }}
          onPaymentUpdated={handlePaymentSaved}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          title="Delete Payment"
          message={`Are you sure you want to delete the payment of ${formatCurrency(deleteTarget.amount)} made on ${formatDateShort(deleteTarget.payment_date)}? This action cannot be undone.`}
          onConfirm={handleDeletePayment}
          onClose={() => setDeleteTarget(null)}
          loading={deleteLoading}
        />
      )}

      {viewPayment && (
        <ViewPaymentModal
          isOpen={!!viewPayment}
          onClose={() => setViewPayment(null)}
          payment={viewPayment}
        />
      )}
    </div>
  );
};

export default ViewBilling;